import { useEffect, useRef, useState, type FormEvent } from "react";
import { Check, Copy, Download, Fingerprint, KeyRound, Laptop, Link2, LockOpen, MonitorSmartphone, RefreshCw, ShieldCheck, Smartphone, Trash2, Upload, X } from "lucide-react";
import QRCode from "qrcode";
import type { AccountDevice, DeviceLinkApproval } from "../types";
import type { VaultProtection } from "../vault";

type DeviceLinkView = Readonly<{ expiresAt: string; url: string }>;

type Props = Readonly<{
  approvals: DeviceLinkApproval[];
  busy: boolean;
  devices: AccountDevice[];
  onClose: () => void;
  onCreateLink: () => Promise<DeviceLinkView>;
  onDecideApproval: (approval: DeviceLinkApproval, decision: "approve" | "reject") => Promise<void>;
  onExport: (passphrase: string) => Promise<void>;
  onImport: (file: File, passphrase: string) => Promise<number>;
  onRefresh: () => Promise<void>;
  onRevoke: (device: AccountDevice) => Promise<boolean>;
  localProtection: VaultProtection;
  onSetLocalProtection: (protection: "automatic" | "passphrase" | "system", passphrase?: string) => Promise<void>;
}>;

function DeviceIcon({ platform }: { platform: AccountDevice["platform"] }) {
  if (platform === "android" || platform === "ios") return <Smartphone />;
  if (platform === "desktop") return <Laptop />;
  return <MonitorSmartphone />;
}

function platformName(platform: AccountDevice["platform"]): string {
  return ({ web: "Браузер", android: "Android", ios: "iPhone / iPad", desktop: "Компьютер" })[platform];
}

export function DeviceManagerModal({ approvals, busy, devices, localProtection, onClose, onCreateLink, onDecideApproval, onExport, onImport, onRefresh, onRevoke, onSetLocalProtection }: Props) {
  const [tab, setTab] = useState<"devices" | "transfer" | "protection">("devices");
  const [error, setError] = useState<string | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [link, setLink] = useState<DeviceLinkView | null>(null);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [exportPassphrase, setExportPassphrase] = useState("");
  const [exportConfirm, setExportConfirm] = useState("");
  const [importPassphrase, setImportPassphrase] = useState("");
  const [lockPassphrase, setLockPassphrase] = useState("");
  const [lockConfirm, setLockConfirm] = useState("");
  const importFile = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setRefreshing(true);
    void onRefresh()
      .catch((caught) => setError(caught instanceof Error ? caught.message : "Не удалось загрузить устройства"))
      .finally(() => setRefreshing(false));
  }, [onRefresh]);

  useEffect(() => {
    if (!link) {
      setQrCode(null);
      return;
    }
    void QRCode.toDataURL(link.url, {
      errorCorrectionLevel: "M",
      margin: 1,
      width: 256,
      color: { dark: "#111827", light: "#ffffff" },
    }).then(setQrCode).catch(() => setError("Не удалось построить QR-код"));
  }, [link]);

  const run = async (work: () => Promise<void>) => {
    setError(null);
    setStatus(null);
    try {
      await work();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Операция не выполнена");
    }
  };

  const createLink = () => run(async () => {
    const created = await onCreateLink();
    setLink(created);
    setStatus("Одноразовая ссылка создана. Она действует 10 минут.");
  });

  const refresh = () => run(async () => {
    setRefreshing(true);
    try {
      await onRefresh();
      setStatus("Список активных устройств обновлён.");
    } finally {
      setRefreshing(false);
    }
  });

  const copyLink = () => run(async () => {
    if (!link) return;
    await navigator.clipboard.writeText(link.url);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 2_000);
  });

  const exportBackup = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      if (exportPassphrase.length < 12) throw new Error("Защитная фраза должна содержать не менее 12 символов");
      if (exportPassphrase !== exportConfirm) throw new Error("Защитные фразы не совпадают");
      await onExport(exportPassphrase);
      setExportPassphrase("");
      setExportConfirm("");
      setStatus("Зашифрованная резервная копия скачана. Сохраните её отдельно от защитной фразы.");
    });
  };

  const selectImport = async (file: File | undefined) => {
    if (!file) return;
    await run(async () => {
      if (!importPassphrase) throw new Error("Введите защитную фразу резервной копии");
      const count = await onImport(file, importPassphrase);
      setStatus(`Восстановлено ключей: ${count}. Доступные чаты обновлены.`);
      setImportPassphrase("");
      if (importFile.current) importFile.current.value = "";
    });
  };

  const enablePassphrase = (event: FormEvent) => {
    event.preventDefault();
    void run(async () => {
      if (lockPassphrase.length < 12) throw new Error("Локальная защитная фраза должна содержать не менее 12 символов");
      if (lockPassphrase !== lockConfirm) throw new Error("Локальные защитные фразы не совпадают");
      await onSetLocalProtection("passphrase", lockPassphrase);
      setLockPassphrase("");
      setLockConfirm("");
      setStatus("Локальная защитная фраза включена. Она будет запрашиваться при следующем открытии приложения.");
    });
  };

  return (
    <div className="modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget && !busy) onClose(); }} role="presentation">
      <section aria-labelledby="devices-title" aria-modal="true" className="modal-card device-manager" role="dialog">
        <header>
          <div><p className="eyebrow">БЕЗОПАСНОСТЬ АККАУНТА</p><h2 id="devices-title">Устройства и ключи</h2></div>
          <button aria-label="Закрыть" className="icon-button" disabled={busy} onClick={onClose} type="button"><X /></button>
        </header>
        <div className="segmented device-tabs" role="tablist">
          <button className={tab === "devices" ? "active" : ""} onClick={() => setTab("devices")} role="tab" type="button">Устройства</button>
          <button className={tab === "transfer" ? "active" : ""} onClick={() => setTab("transfer")} role="tab" type="button">Перенос и копия</button>
          <button className={tab === "protection" ? "active" : ""} onClick={() => setTab("protection")} role="tab" type="button">Защита приложения</button>
        </div>
        {error && <div className="form-error" role="alert">{error}</div>}
        {status && <div className="form-success" role="status"><Check />{status}</div>}

        {tab === "devices" ? (
          <div className="device-list">
            {approvals.map((approval) => (
              <article className="device-approval" key={approval.id}>
                <div className="device-approval-head"><Link2 /><span><b>Запрос на подключение</b><small>{approval.deviceName} · {platformName(approval.platform)}</small></span></div>
                <p>Сравните код с кодом на новом устройстве:</p>
                <strong>{approval.approvalCode.slice(0, 3)} {approval.approvalCode.slice(3)}</strong>
                <small className="device-approval-fingerprint" title={approval.fingerprint}>Отпечаток: {approval.fingerprint.slice(0, 28)}…</small>
                <div className="device-approval-actions">
                  <button className="primary-button compact secondary" disabled={busy} onClick={() => void run(async () => { await onDecideApproval(approval, "reject"); setStatus("Подключение отклонено."); })} type="button"><X />Отклонить</button>
                  <button className="primary-button compact" disabled={busy} onClick={() => void run(async () => { await onDecideApproval(approval, "approve"); setStatus("Подключение разрешено. Ожидаем завершение на новом устройстве."); })} type="button"><Check />Разрешить</button>
                </div>
              </article>
            ))}
            <div className="device-list-head"><span>Активные устройства</span><button aria-label="Обновить список активных устройств" className="icon-button" disabled={busy || refreshing} onClick={() => void refresh()} title="Обновить" type="button"><RefreshCw className={refreshing ? "spin-icon" : undefined} /></button></div>
            {devices.length === 0 && !refreshing && <p className="empty-device-list">Активные устройства не найдены.</p>}
            {devices.filter((device) => !device.revokedAt).map((device) => (
              <article className="device-row" key={device.id}>
                <span className="device-icon"><DeviceIcon platform={device.platform} /></span>
                <span className="device-copy">
                  <b>{device.name}{device.current && <em>Текущее</em>}</b>
                  <small>{platformName(device.platform)} · последний вход {new Date(device.lastSeenAt).toLocaleString("ru-RU")}</small>
                  <code title={device.fingerprint}>{device.fingerprint.slice(0, 24)}…</code>
                </span>
                {!device.current && <button aria-label={`Отозвать ${device.name}`} className="icon-button danger" disabled={busy} onClick={() => void run(async () => { if (await onRevoke(device)) setStatus("Устройство отозвано и удалено из списка, ключи активных чатов обновлены."); })} title="Отозвать устройство" type="button"><Trash2 /></button>}
              </article>
            ))}
            <p className="security-note"><ShieldCheck /> При отзыве устройства для каждого активного чата создаётся новый ключ. Отозванное устройство больше не сможет получить новые сообщения.</p>
          </div>
        ) : tab === "transfer" ? (
          <div className="device-transfer">
            <section className="transfer-card">
              <div className="transfer-heading"><Link2 /><span><b>Подключить новое устройство</b><small>Откройте ссылку или QR-код и подтвердите одинаковый код здесь. Новое устройство сохранит ключи для автоматического входа; при необходимости дополнительную блокировку можно включить во вкладке «Защита приложения».</small></span></div>
              {!link ? <button className="primary-button compact" disabled={busy} onClick={() => void createLink()} type="button">Создать одноразовый QR-код</button> : (
                <div className="device-link-result">
                  {qrCode && <img alt="QR-код для подключения нового устройства" src={qrCode} />}
                  <div className="device-link-copy"><input aria-label="Одноразовая ссылка" readOnly value={link.url} /><button className="icon-button" onClick={() => void copyLink()} title="Копировать" type="button">{copied ? <Check /> : <Copy />}</button></div>
                  <small>Действует до {new Date(link.expiresAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" })}. Секрет находится после символа # и не отправляется серверу.</small>
                </div>
              )}
            </section>

            <section className="transfer-card">
              <div className="transfer-heading"><Download /><span><b>Экспорт резервной копии</b><small>Скачивает ключи чатов в файл, зашифрованный отдельной защитной фразой.</small></span></div>
              <form className="recovery-form" onSubmit={exportBackup}>
                <label><span>Защитная фраза</span><input autoComplete="new-password" minLength={12} onChange={(event) => setExportPassphrase(event.target.value)} required type="password" value={exportPassphrase} /></label>
                <label><span>Повторите защитную фразу</span><input autoComplete="new-password" minLength={12} onChange={(event) => setExportConfirm(event.target.value)} required type="password" value={exportConfirm} /></label>
                <button className="primary-button compact" disabled={busy} type="submit"><Download />Скачать зашифрованную копию</button>
              </form>
            </section>

            <section className="transfer-card">
              <div className="transfer-heading"><Upload /><span><b>Восстановление из файла</b><small>Ключи расшифровываются в браузере и запечатываются для текущего устройства.</small></span></div>
              <div className="recovery-form">
                <label><span>Защитная фраза копии</span><input autoComplete="current-password" onChange={(event) => setImportPassphrase(event.target.value)} type="password" value={importPassphrase} /></label>
                <input accept=".jtuc,.json,application/json" hidden onChange={(event) => void selectImport(event.target.files?.[0])} ref={importFile} type="file" />
                <button className="primary-button compact secondary" disabled={busy || !importPassphrase} onClick={() => importFile.current?.click()} type="button"><KeyRound />Выбрать файл копии</button>
              </div>
            </section>
          </div>
        ) : (
          <div className="device-protection">
            <section className={`protection-current protection-${localProtection}`}>
              {localProtection === "automatic" || localProtection === "none" ? <LockOpen /> : localProtection === "system" ? <Fingerprint /> : <KeyRound />}
              <span>
                <b>{localProtection === "automatic" || localProtection === "none" ? "Автоматический вход" : localProtection === "system" ? "Системная блокировка" : "Локальная защитная фраза"}</b>
                <small>{localProtection === "automatic" || localProtection === "none"
                  ? "После первого входа приложение открывается без графического ключа, пароля или локальной фразы. Ключи остаются зашифрованы хранилищем устройства."
                  : localProtection === "system"
                    ? "При открытии потребуется биометрия, системный PIN или Windows Hello."
                    : "При открытии потребуется отдельная локальная защитная фраза."}</small>
              </span>
            </section>

            <section className="transfer-card">
              <div className="transfer-heading"><LockOpen /><span><b>Не спрашивать код при открытии</b><small>Рекомендуемый обычный режим. Включайте дополнительный запрос ниже только если он действительно нужен на этом устройстве.</small></span></div>
              <button className="primary-button compact" disabled={busy || localProtection === "automatic"} onClick={() => void run(async () => { await onSetLocalProtection("automatic"); setStatus("Автоматический вход включён. При следующем запуске дополнительный код не потребуется."); })} type="button">Включить автоматический вход</button>
            </section>

            <section className="transfer-card">
              <div className="transfer-heading"><Fingerprint /><span><b>Биометрия или системный PIN</b><small>Дополнительная защита через Android/iOS, Windows Hello или Touch ID. Запрос появится при следующем открытии.</small></span></div>
              <button className="primary-button compact secondary" disabled={busy || localProtection === "system"} onClick={() => void run(async () => { await onSetLocalProtection("system"); setStatus("Системная блокировка включена."); })} type="button"><Fingerprint />Включить системную блокировку</button>
            </section>

            <section className="transfer-card">
              <div className="transfer-heading"><KeyRound /><span><b>Локальная защитная фраза</b><small>Отдельная фраза только для этого устройства. Не используйте пароль аккаунта и обязательно сохраните её.</small></span></div>
              <form className="recovery-form" onSubmit={enablePassphrase}>
                <label><span>Новая локальная фраза</span><input autoComplete="new-password" minLength={12} onChange={(event) => setLockPassphrase(event.target.value)} required type="password" value={lockPassphrase} /></label>
                <label><span>Повторите локальную фразу</span><input autoComplete="new-password" minLength={12} onChange={(event) => setLockConfirm(event.target.value)} required type="password" value={lockConfirm} /></label>
                <button className="primary-button compact secondary" disabled={busy} type="submit"><KeyRound />Включить запрос фразы</button>
              </form>
            </section>
            <p className="security-note"><ShieldCheck /> Настройка действует только на этом устройстве и не меняет пароль аккаунта или сквозное шифрование сообщений.</p>
          </div>
        )}
      </section>
    </div>
  );
}
